// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/ui/Button";
import { createTrackTextTranslation, deleteTrackLanguage, getTrackLanguages } from "../../../../api";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import { DropdownOption } from "../../../../types/screensTypes";
import { LanguageCard } from "../../../../components/cards/LanguageCard";
import stepStyles from "../createStep.module.css";

interface Translation {
    language: string;
    languageName: string;
}

const TranslateTrack: React.FC = () => {
    const { trackData } = useTrackCreation();
    const { data, loading, error } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [translations, setTranslations] = useState<Translation[]>([]);
    const [loadingTranslation, setLoadingTranslation] = useState<boolean>(false);
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [nonExistingLanguages, setNonExistingLanguages] = useState<string[]>([]);
    const [selectedNewLanguage, setSelectedNewLanguage] = useState<string>("");
    const [displayError, setDisplayError] = useState<boolean>(false);
    const [displaySuccess, setDisplaySuccess] = useState<boolean>(false);

    // Format global language options for the dropdown.
    const languagesAIJsonFormatted: DropdownOption[] = (data.languagesAI as DropdownOption[] | undefined) || [];

    // Get a list of languages already added.
    const selectedLanguages: string[] = translations.map((t) => t.language);
    const baseLanguage: string = trackData.language || "EN";

    // Filter languages available for selection.
    const getFilteredLanguagesForDropdown = (): DropdownOption[] => {
        return languagesAIJsonFormatted.filter(
            (lang) =>
                lang.value !== baseLanguage &&
                !selectedLanguages.includes(lang.value) &&
                !existingLanguages.includes(lang.label) &&
                (nonExistingLanguages.includes(lang.value) || nonExistingLanguages.includes(lang.label)),
        );
    };

    // Add a new language translation.
    const handleAddNewLanguage = (newLanguage: string): void => {
        if (newLanguage) {
            const languageObj = data.languagesAI.find((lang: DropdownOption) => lang.value === newLanguage);
            setTranslations((prev) => [
                ...prev,
                {
                    language: newLanguage,
                    languageName: languageObj?.label || newLanguage,
                },
            ]);
            setSelectedNewLanguage("");
        }
    };

    // Request translation creation for each selected language.
    const translateTrack = async (): Promise<void> => {
        setLoadingTranslation(true);
        try {
            const token = localStorage.getItem("token");
            const trackId = trackData.trackId;
            if (!token || !trackId) {
                setLoadingTranslation(false);
                return;
            }
            const translationsToSave = translations.filter((t) => t.language);
            for (const translation of translationsToSave) {
                const tran = await createTrackTextTranslation({
                    token,
                    trackId,
                    language: translation.language,
                });

                console.log("TRANSLATION", tran);
                if (!tran) {
                    setDisplayError(true);
                } else {
                    setDisplayError(false);
                    setDisplaySuccess(true);
                    setTimeout(() => setDisplaySuccess(false), 3000);
                }
                setTranslations([]);
                await getTrackLanguagesAPI();
            }
            setLoadingTranslation(false);
        } catch (err) {
            console.error("Error saving translations", err);
            setDisplayError(true);
            setLoadingTranslation(false);
        }
    };

    // Remove a translation from the list.
    const deleteTranslation = (index: number): void => {
        const newTranslations = [...translations];
        newTranslations.splice(index, 1);
        setTranslations(newTranslations);
    };

    // Delete an existing language translation.
    const deleteExistingTranslation = async (languageLabel: string): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        const langObj = data.languagesAI.find((lang: DropdownOption) => lang.label === languageLabel);
        if (!langObj) return;
        if (langObj) {
            await deleteTrackLanguage({ token, trackId, language: langObj.value });
            await getTrackLanguagesAPI();
        }
    };

    // Fetch track languages to update existing and non-existing languages.
    const getTrackLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        const response = await getTrackLanguages({ token, id: trackId });
        const languages = response?.formattedLanguages || [];
        const arrayLang: string[] = [];
        for (let i = 0; i < languages.length; i++) {
            const lang = languages[i];
            const langObj = data.languagesAI.find((l: DropdownOption) => l.value === lang);
            if (langObj) arrayLang.push(langObj.label);
        }
        setExistingLanguages(arrayLang);
        const nonExisting = response?.formattedContentLocalizedLanguages || [];
        const arrayNonExisting: string[] = [];
        for (let i = 0; i < nonExisting.length; i++) {
            const lang = nonExisting[i];
            const langObj = data.languagesAI.find((l: DropdownOption) => l.value === lang);
            if (langObj) arrayNonExisting.push(langObj.label);
        }
        setNonExistingLanguages(arrayNonExisting);
    };

    const activateProductByTrackIdAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        await translateTrack();
    };

    useEffect(() => {
        getTrackLanguagesAPI();
    }, []);

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${stepStyles.stepPublish}`}>
            <TitleSubtitle
                title={t("languagePublication")}
                subtitle={t("addLanguagesWillBeAvailableToYourUsersWithinTheApplication")}
                tooltip={t("doNotTranslateTooltip")}
                layout="publish"
            />
            <div>
                {existingLanguages.length > 0 && (
                    <div className={`experience-publish-block ${stepStyles.publishBlock}`}>
                        <h5 className={`experience-publish-h ${stepStyles.publishHeading}`}>
                            {t("publishedLanguages")}
                        </h5>
                        <div className={`experience-publish-chips ${stepStyles.publishChips}`}>
                            {existingLanguages.map((lang) => (
                                <LanguageCard
                                    key={lang}
                                    language={lang}
                                    onRemoveLanguage={async () => await deleteExistingTranslation(lang)}
                                />
                            ))}
                        </div>
                    </div>
                )}
                <div className={`experience-publish-block ${stepStyles.publishBlock}`}>
                    <h5 className={`experience-publish-h ${stepStyles.publishHeading}`}>{t("publishANewLanguage")}</h5>
                    <div className={`experience-publish-sel ${stepStyles.publishSel}`}>
                        <Dropdown
                            label=""
                            options={getFilteredLanguagesForDropdown()}
                            onChange={(value: string) => handleAddNewLanguage(value)}
                            loading={loading}
                            error={error ? true : false}
                            value={selectedNewLanguage}
                            placeholder={t("selectALanguage")}
                            isWithoutMargin
                        />
                    </div>
                </div>
                {translations.length > 0 && (
                    <div className={`experience-publish-block ${stepStyles.publishBlock}`}>
                        <h5 className={`experience-publish-h ${stepStyles.publishHeading}`}>
                            {t("selectedLanguages")}
                        </h5>
                        <div className={`experience-publish-chips ${stepStyles.publishChips}`}>
                            {translations.map((translation, index) => (
                                <LanguageCard
                                    key={index}
                                    language={translation.languageName}
                                    onRemoveLanguage={() => deleteTranslation(index)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <div>
                    <Button
                        label={
                            displaySuccess
                                ? t("successfullPublished")
                                : loadingTranslation
                                  ? t("publishing")
                                  : t("publish")
                        }
                        onClick={async () => {
                            await activateProductByTrackIdAPI();
                        }}
                        disabled={!trackData.title}
                        variant="primary"
                        shape="pill"
                        className={stepStyles.continueBtn}
                    />
                </div>
            </div>
            {displayError && <div className="error-message">{t("missingInfoToPublishYourTour")}</div>}
        </div>
    );
};

export default TranslateTrack;
